import type { Observation } from './observation.js';
/**
 * Wire types shared by the publisher phone, the director UI and the server.
 * One definition, both sides, no drift.
 */

/** Re-exported so wire-type consumers need only this module. */
export type { Observation } from './observation.js';

/** Mechanical measurements a publishing phone computes about its own feed. */
export interface FeedVitals {
  /** Publisher-local monotonic capture time, ms since the page loaded. */
  t: number;
  /** Wall clock at send. A bound on capture time, never capture time itself. */
  wall: number;
  /**
   * Wall clock when the frame was CAPTURED, not when it was sent.
   *
   * The anchor that lets two feeds be compared without a blind audio search.
   * From `VideoFrameCallbackMetadata.captureTime` where the browser provides
   * it, null where it does not, which is not a failure: a null falls back to
   * correlating over the full offset range.
   *
   * Publisher-supplied, and therefore a CLAIM. `shared/align.ts` checks it
   * against the audio rather than trusting it, so a convenient offset that the
   * content does not support returns `unalignable` naming the claim. This is
   * why the field does not violate the rule against accepting anything from a
   * publisher: it proposes, and the content decides.
   */
  capturedAt: number | null;

  // --- transport, from RTCPeerConnection.getStats() ---
  /** Outbound video bitrate, bits per second, averaged over the last window. */
  bitrate: number;
  /** Fraction of packets lost in the last window, 0..1. */
  packetLoss: number;
  /** Round trip time in ms, or null when the stat is unavailable. */
  rttMs: number | null;
  /** Encoder frames per second actually sent. */
  fps: number;
  frameWidth: number;
  frameHeight: number;
  /** Cumulative seconds the receiver would have seen frozen. */
  freezeSeconds: number;
  /** "cpu" | "bandwidth" | "none" | "other", straight from getStats. */
  qualityLimitationReason: string;

  // --- picture, from an OffscreenCanvas sampled off the raw track ---
  /** Mean luma 0..1. Too low is a dark gym, too high is a blown-out window. */
  luma: number;
  /** Fraction of sampled pixels at either rail, 0..1. */
  clipping: number;
  /** Mean absolute inter-frame difference 0..1. Proxy for camera shake. */
  motion: number;
  /** Variance of recent motion. High means shaky, near zero means locked off. */
  shake: number;

  // --- sound, from an AnalyserNode on the mic track ---
  /**
   * Whether the publisher is capturing sound AT ALL: a track exists, it has not
   * ended, and the source is still delivering samples.
   *
   * This is the hard gate, and `audioRms` is not. A covered mic and a quiet
   * room both read an RMS near zero, and they are not the same event: one is an
   * instrument that has stopped working, the other is a room. An instrument
   * failure is never a judgment, and gating on the level made the loudness of
   * the room a standards question. Measured 2026-08-29 in a quiet room: 948 of
   * 995 samples sat below the old 0.01 floor with a working microphone, so the
   * gate was refusing feeds for being indoors.
   *
   * A publisher that sends nothing here is treated as not capturing, because a
   * missing field is not evidence of sound.
   */
  audioTrackLive: boolean;
  /**
   * RMS level 0..1 over the last window.
   *
   * The two ends are different questions and this comment claimed only one of
   * them until 2026-08-30. Quiet RANKS: quiet costs position, silence does not
   * cost eligibility, because a covered microphone and a quiet room read the
   * same level and only one is a fault. Loud REFUSES: `scoreFeed` returns
   * `audio too hot` above `audioRmsRange[1]`, which is a hard gate.
   */
  audioRms: number;
  /** Fraction of audio samples at full scale, 0..1. */
  audioClipping: number;

  // --- device health, so we can tell throttling apart from a bad operator ---
  batteryLevel: number | null;
  thermalHint: 'nominal' | 'throttled' | 'unknown';
}

import type { AudienceState } from './audience.js';
import type { DelayAudit } from './delay.js';
import type { FilterAudit } from './cascade.js';
import type { Alignment } from './align.js';
import type { FeedVerdict } from './observation.js';
import type { BroadcastDecision } from './derivation.js';
import type { Feedback } from './feedback.js';

/**
 * A MediaMTX path name a publisher may claim.
 *
 * **Validated because wave 2 made it a FILESYSTEM path.** Before, `feed.path`
 * only ever went into an RTSP URL after the authority, where it cannot escape.
 * P1 reads recorded segments out of `recordings/media/<path>`, and
 * `path.join('recordings/media', '../../../../../../../../tmp/x')` leaves the
 * repository entirely: verified on 2026-08-29 by registering that path and
 * watching the server list files under `/tmp`.
 *
 * The ingest gate refuses such a feed, because MediaMTX has no such path, so
 * nothing reached air. That is one remote API call standing between an
 * unauthenticated socket message and an arbitrary directory read, which is not
 * where this invariant should rest. `segments.ts` refuses an escaping path too:
 * two layers, because "the server takes its own samples" is the load-bearing one.
 *
 * MediaMTX path names are alphanumerics, underscore, dash and dot, optionally
 * nested with `/`. A leading dot or any `..` segment is refused outright.
 */
export const PATH_PATTERN = /^[A-Za-z0-9_-][A-Za-z0-9_.-]*(?:\/[A-Za-z0-9_-][A-Za-z0-9_.-]*)*$/;

export function isSafePath(p: string): boolean {
  if (p.length === 0 || p.length > 128) return false;
  // ONE mechanism, not two. The first version also split on `/` and refused a
  // `..` or `.` segment explicitly, and `npm run test:proofs` showed that line
  // could be deleted with the suite still green: every segment must START with
  // `[A-Za-z0-9_-]`, so `..` and `.` were already impossible. A check that cannot
  // fail looks like protection and is not, which is the third time that lesson
  // has been paid for in this repository.
  return PATH_PATTERN.test(p);
}

/**
 * What the media plane says is feeding a path.
 *
 * A wire type rather than a server one, because a publisher refused for ingest
 * provenance has to be told so: "awaiting picture review" forever is precisely
 * the "told the wrong thing about itself" failure proposal 2.4 is about. The
 * fetching lives in `server/ingest.ts`; only the answer crosses the wire.
 */
/**
 * What stands in place of provenance where the question cannot be asked.
 *
 * Every field here is WEAKER than an ingest check and the type says so rather
 * than leaving a reader to assume otherwise. A fingerprint proves two arrivals
 * are the same bytes and nothing about where they came from; a submitter is a
 * claim an account makes; container metadata is trivially forgeable. They are
 * carried so a refusal can cite something, never so a gate can pass on them.
 */
export interface IngestSubstitutes {
  /** Content fingerprint taken at receipt. Null until something computes one. */
  contentHash: string | null;
  /** The account the submission is attributed to. Null when unattributed. */
  submitter: string | null;
  /** Container metadata as reported by the file itself. Forgeable, and labelled. */
  container: string | null;
}

/**
 * THREE states, not two, and the third is the one this repository keeps
 * getting wrong in other places.
 *
 * `direct`      MediaMTX says a publisher opened this path itself.
 * `refused`     something answered and the answer disqualifies this feed.
 * `not-askable` there is no ingest event to ask about, because this entry point
 *               is a stored file rather than a live push.
 *
 * The third is NOT a pass and NOT a refusal. Collapsing it into `direct` would
 * certify provenance nobody established, which is the instrument-as-judgment
 * failure this repository has eight recorded instances of. Collapsing it into
 * `refused` would make the submitted entry point permanently unusable, which is
 * what stage 1 measured before stage 3 existed.
 *
 * `docs/plans/2026-09-03-submission-as-a-second-entry-point.md` §4.
 */
export type IngestState = 'direct' | 'refused' | 'not-askable';

export interface IngestCheck {
  state: IngestState;
  /** Whatever the API reported, verbatim, so an unexpected type is diagnosable. */
  sourceType: string | null;
  reason: string;
  /**
   * Only ever present on `not-askable`. Null on both other states, because a
   * substitute beside a real ingest answer would read as corroboration and it
   * is the opposite: it is what exists when the answer does not.
   */
  substitutes: IngestSubstitutes | null;
  checkedAt: number;
}

/** Provenance was ESTABLISHED. Only `direct` may ever return true here. */
export function ingestVerified(c: IngestCheck): boolean {
  return c.state === 'direct';
}

/**
 * The gate's answer, which is deliberately not the inverse of `ingestVerified`.
 *
 * `not-askable` abstains: it refuses nothing and certifies nothing. Writing this
 * as `!ingestVerified(c)` is the single edit that would silently turn the
 * submitted entry point off again, which is why it is a named function with its
 * own guard rather than an expression at three call sites.
 */
export function ingestRefuses(c: IngestCheck): boolean {
  return c.state === 'refused';
}

/**
 * MAY THIS SAMPLE PROCEED. An ALLOWLIST, which is what the gate must consult.
 *
 * `ingest.ts` states the house rule the gate exists under: an allowlist rather
 * than a list of things to refuse, because a MediaMTX release that adds a new
 * pull type would silently pass a denylist. Stage 3 broke that. Replacing
 * `direct ? pass : fail` with `ingestRefuses(c) ? fail : pass` reads as the same
 * change of shape and is the opposite polarity: the gate stopped refusing on the
 * ABSENCE OF A PASS and started passing on the ABSENCE OF A REFUSAL.
 *
 * Live-neutral while the union has three members, and a fourth is the failure: a
 * relay, a backhaul, or a `pending` re-check would be admitted, recorded as
 * passed in the ruling record, and reach air with provenance never established.
 * Found by a live-path review 2026-09-03, which also noted `ingestVerified` had
 * no caller in `src/` at all: the positive check existed and was never asked.
 *
 * Enumerated rather than negated, so a new state defaults to REFUSED and the
 * compiler does not have to be relied on to notice.
 */
export function ingestAdmits(c: IngestCheck): boolean {
  return c.state === 'direct' || c.state === 'not-askable';
}

/** A publisher registered with the control server. */
export interface Feed {
  id: string;
  /** MediaMTX path this publisher is pushing to. */
  path: string;
  /** Human label, e.g. "Row 4, home side". */
  /**
   * WHICH ANGLE this is: "row 4, home side". Free text a publisher writes so a
   * viewer can tell nine streams of one court apart, and the one field that
   * genuinely varies across every stream at one event.
   *
   * Called `label` until 2026-08-30, which collided with the director's
   * `label` FRAME, an entirely different thing that records a decision as good
   * or bad. In the director's own code `tile.label` and `type: 'label'` sat
   * feet apart meaning a stream's name and a verdict on a cut.
   *
   * Renamed rather than commented, because three words for one field (`label`
   * in code, "perspective" in the plan, "angle" in the interface) is the same
   * defect as the venue naming that outlived its own model.
   */
  angle: string;
  /** Publisher-declared operator name, shown in the director UI only. */
  operator: string;
  /**
   * The channels this feed is offered to, §5.
   *
   * **Empty means on no channel**, which is the plan's rule rather than a
   * default: in event mode nothing asks, and in channel mode a feed that
   * declared nothing is in front of no board. Stored as the publisher's claim,
   * filtered to channels this node actually keeps a board for, because being
   * offered to a channel nobody defines is the same as being offered to none.
   */
  channels: readonly string[];
  /**
   * The account behind this feed, when there is one.
   *
   * Null for an anonymous publisher, which is the common case and not a
   * degraded one. `operator` above is a CLAIM the publisher typed; `handle` is
   * one the database made unique. Where both exist the handle is the one that
   * identifies, and it is what an archive row hangs off.
   */
  handle: string | null;
  profileId: string | null;
  connectedAt: number;
  lastVitalsAt: number | null;
  vitals: FeedVitals | null;
  score: FeedScore | null;
  /**
   * This stream's OWN location, or null meaning "the session's".
   *
   * Null rather than a copy of the default, deliberately. A copy is a second
   * answer that goes stale the moment the default is corrected mid-event, and
   * the effective location is resolved where it is read.
   */
  location: Location | null;
  /** Independent picture review. Null means never reviewed, which is not approval. */
  visual: Observation | null;
  /** Independent speech review. Null means never reviewed, which is not approval. */
  speech: Observation | null;
  /** Rolling audience signal. Never acts immediately: see shared/audience.ts. */
  audience: AudienceState;
  /**
   * Where this feed sat against the reference in the last closed window.
   *
   * Null means P2 has not ruled yet, which is not the same as `unalignable`:
   * one is silence and the other is a finding, and collapsing them is how a
   * feed that was never compared gets reported as having lost.
   */
  alignment: Alignment | null;
  /** P3's last committed derivation for this feed. Null before the first window. */
  verdict: FeedVerdict | null;
  /**
   * What P1 found out about this feed's ingest provenance, or null before it has
   * sampled once. Null is NOT "direct": the gate that refuses lives in P1, and
   * this field exists so the refusal has a name a publisher can act on.
   */
  ingest: IngestCheck | null;
}

/** Result of running FeedVitals through the shared standard. */
export interface FeedScore {
  /** Weighted 0..1 roll-up of the sub-scores. Only meaningful when passing. */
  overall: number;
  transport: number;
  picture: number;
  sound: number;
  /** True when every hard gate is satisfied. A feed that fails cannot go on air. */
  passes: boolean;
  /** Human-readable reasons this feed is failing, empty when passing. */
  failures: string[];
}

/** What the viewer page is currently being told to show. */
/**
 * A feed as BROADCAST: the stored record plus the server's own answer about it.
 *
 * `eligible` and `blockers` are computed at snapshot time by the same
 * `eligibility()` call `take()` refuses on. They are sent rather than left to
 * the client for a reason that is not code duplication: the predicate compares
 * `lastVitalsAt` and verdict ages against `now`, and every one of those
 * timestamps is the SERVER's. A director evaluating them against a browser
 * clock gets a different answer from the server with no drift in the code at
 * all, which is worse than duplication because nothing about it is visible.
 *
 * Until 2026-08-29 the director derived a Take button from `score.passes`
 * alone, which is the mechanical gate. That enabled the button for any feed
 * awaiting review, and the server refused the take in silence.
 */
/**
 * Where a stream is, alongside the perspective that says which angle it is.
 *
 * Wave 6. `angle` answers "which angle is this" and varies across every stream
 * at one event, which is its whole job. This answers "where is this" and usually
 * does not vary, which is why it is DEFAULTED from the session rather than asked
 * of every publisher: at a gym nobody types anything. A parade is the case that
 * killed the venue entity this plan started with, because its streams are
 * genuinely in different places and a venue has no way to say so.
 *
 * Deliberately small, and OURS. Google's own payload lives in `place_cache` and
 * is never rendered: see the plan's section 2b. Nothing on a surface a viewer
 * sees, and nothing in the archive, reads a Google field.
 */
export interface Location {
  /**
   * Google's place id, and the only Google-originated value in this record.
   *
   * The explicit indefinite exception to the caching restriction, and the whole
   * reason to use Places: two typings of one gym become provably the same place,
   * which a string match across `Chugiak High`, `chugiak hs` and
   * `Chugiak High School Gym` never will.
   *
   * Null when somebody typed a location without picking a suggestion, which
   * stays first-class: a rec hall may not be in Places at all.
   */
  placeId: string | null;
  /** "Chugiak High School". Ours, stored, and what actually renders. */
  name: string;
  /** "Chugiak, AK". Optional, ours, and never parsed out of `name`. */
  locality: string | null;
  /**
   * How precise `name` is, so a region is not described as a building.
   *
   * A state and a street corner are otherwise both just a place, and nothing
   * downstream can tell that one of them is useless.
   */
  precision: 'venue' | 'locality' | 'region';
  /** Whether a human typed this freehand or picked it from Places. */
  source: 'typed' | 'picked';
}

/**
 * A publisher's typed location, normalised into a `Location`, or null.
 *
 * One place, so the socket handler does not decide what a publisher's location
 * means. A blank name is null rather than an empty-named location: a publisher
 * who sends nothing meaningful gets the session's default, which is the same
 * outcome as not sending the field at all.
 *
 * Always `placeId: null` and `source: 'typed'`. A publisher types and never
 * picks: the picker is the newsroom's surface, and only it may write 'picked'.
 * `precision` is 'venue' because a person saying where they are is naming a
 * place, not a region.
 */
export function locationFromPublisher(
  input: { name?: string; locality?: string | null } | undefined,
): Location | null {
  const name = (input?.name ?? '').trim();
  if (name === '') return null;
  const locality = (input?.locality ?? '').trim();
  return {
    placeId: null,
    name,
    locality: locality === '' ? null : locality,
    precision: 'venue',
    source: 'typed',
  };
}

/**
 * What a stream's location actually IS: its own, or the session's.
 *
 * Resolved at READ time and never copied onto the feed. A copy is a second
 * answer that goes stale the moment the default is corrected, and resolving at
 * write time is the mistake that would make every stream at a parade report the
 * gym.
 */
export function effectiveLocation(
  feed: { location: Location | null } | null,
  sessionDefault: Location | null,
): Location | null {
  return feed?.location ?? sessionDefault;
}

/**
 * How a location reads on a surface, or null when there is nothing to say.
 *
 * Null rather than an empty string, because the caller must not render a
 * heading at all when there is no location: that is LOC1, and "no location
 * configured" is a working state rather than a degraded one.
 *
 * `precision` is honoured rather than decorative. A region is not a place an
 * event happened AT, so it is never rendered as though it were a building,
 * which is LOC8.
 */
export function locationLabel(location: Location | null): string | null {
  if (location === null) return null;
  const name = location.name.trim();
  if (name === '') return null;
  if (location.precision === 'region') return name;
  const locality = (location.locality ?? '').trim();
  return locality === '' || locality === name ? name : `${name}, ${locality}`;
}

/**
 * The session's default location, read once at boot.
 *
 * A switchboard instance is brought up for a game and taken down after it, so
 * one server run is one event and the default is constant for its life. Unset
 * is a WORKING configuration: no location is a state every surface renders, not
 * a degraded one, and it never gates a publisher reaching air.
 *
 * `placeId` is deliberately absent here. An env var cannot hold one a human
 * recognises, and asking an operator to paste `ChIJN1t_tDeuEmsRUsoyG83frY4`
 * into `.env` is worse than letting the picker supply it later.
 */
export function locationFromEnv(env: NodeJS.ProcessEnv = process.env): Location | null {
  const name = (env.LOCATION_NAME ?? '').trim();
  if (name === '') return null;
  const locality = (env.LOCATION_LOCALITY ?? '').trim();
  const declared = (env.LOCATION_PRECISION ?? '').trim();
  const precision: Location['precision'] =
    declared === 'locality' || declared === 'region' ? declared : 'venue';
  return {
    placeId: null,
    name,
    locality: locality === '' ? null : locality,
    precision,
    // Typed, because nobody picked it from anything. The picker is stage 3 and
    // is the only thing that may ever write 'picked'.
    source: 'typed',
  };
}

export interface FeedView extends Feed {
  /**
   * Would `take()` accept this feed: **the PICTURE gates, and nothing about the
   * sound.**
   *
   * A control is an advertised action, so this is the predicate the server
   * would refuse on and not a proxy for it. The picture and sound split moved
   * `Switchboard.take()` from `eligibility()` to `pictureEligibility()` and
   * left this field on the old one, which inverted the very defect guards `D1`
   * to `D3` were written for: instead of a button offering what the server
   * would refuse, the button was DISABLED for a feed the server would happily
   * take, so the one feature the split exists to provide (a good picture
   * reaching air with its sound removed) was unreachable from the only page
   * that can trigger it.
   */
  eligible: boolean;
  /** Every blocker, in the order a person would want to hear them. Empty when eligible. */
  blockers: string[];
  /**
   * Will it carry its OWN sound, or go out silent.
   *
   * Separate rather than folded in, because they are separate decisions and a
   * director needs the second one to read the first: a feed that is takeable
   * and silent looks identical to a feed that is takeable and audible unless
   * something says so.
   */
  soundEligible: boolean;
  /** Why the sound will not be its own. Empty when it will be. */
  soundBlockers: string[];
}

/**
 * What is going out, and since when.
 *
 * `silent` is stage 2 of
 * `docs/plans/2026-08-30-picture-and-sound-as-two-decisions.md`. A feed whose
 * PICTURE clears the standard and whose SOUND does not goes to air without its
 * audio, rather than taking an acceptable picture off air because of a sound.
 * The slate is for when nothing is worth watching; silence is for when nothing
 * is worth hearing, and they are different refusals.
 *
 * **A boolean and not a second source, deliberately.** Stage 2 has one feed.
 * Stage 3 chooses a sound and replaces this with a source, at which point
 * `silent: true` becomes `sound: null`, which is the same fact spelled for the
 * model that comes after it.
 *
 * **Required, not optional.** An omitted flag would default to "carry the
 * audio", which is the direction that ships material the gate refused, and this
 * repository has already paid for a control that advertised its enablement
 * without its state. A caller has to say.
 */
export type OnAir =
  | {
    kind: 'feed';
    feedId: string;
    /**
     * Which feed supplies the SOUND, and it is not always this one.
     *
     * Stage 3 replaced stage 2's `silent: boolean` with the source itself, which
     * the plan anticipated: `silent: true` is `sound: null` spelled for the model
     * that came after it. Three values, three broadcasts:
     *
     * - `=== feedId`: ordinary. One publisher, one stream, no process.
     * - `null`: silent. The picture cleared the standard and no sound did.
     * - anything else: this picture with that feed's sound, which is the case
     *   the whole plan exists for: one good angle, somebody talking over it, and
     *   a second phone nearer the floor with clean ambience.
     */
    sound: string | null;
    /**
     * How far the chosen sound is from the picture, in milliseconds, so the mix
     * can correct it. Positive means the sound is AHEAD.
     *
     * Zero for an ordinary or a silent broadcast. Carried on the state rather
     * than recomputed by the remux, because it is the SWITCHER's measurement:
     * a second derivation of it would be a second copy of the one number that
     * makes the two feeds the same moment.
     */
    soundOffsetMs: number;
    since: number;
    reason: string;
  }
  | {
    kind: 'slate';
    since: number;
    reason: string;
    /**
     * The clause a viewer may be told about, or absent.
     *
     * **ALREADY FILTERED, and that is why it lives on the wire type at all.**
     * This frame reaches every socket including a viewer's, so a field holding
     * the raw code would publish `visual.child-distress` to everybody watching
     * the moment that gate fired. `publicClause` is the only thing that writes
     * it and returns null for all eight `disclosure: 'operator'` clauses, so an
     * operator code cannot reach here by construction rather than by a filter
     * somebody has to remember at the edge. P2.12, issue 024, guards `PC10` to
     * `PC12`.
     */
    clause?: string;
    /**
     * How many clauses fired that a viewer may NOT be told about.
     *
     * A count is not a disclosure: it says a refusal happened and says nothing
     * about whom it was about, which is what makes an unnameable refusal
     * visible as a refusal rather than as silence.
     */
    withheld?: number;
    /**
     * The allowlisted mark and sentence, present on a frame sent to anybody who
     * is not the newsroom.
     *
     * `reason` is composed from blockers and a blocker carries the rubric line
     * verbatim, so the raw one may not leave the process. `publicOnAir` fills
     * these and overwrites `reason` with the safe sentence; the newsroom's
     * frame has neither and keeps the raw reason, because a director is
     * deciding and needs to read what the reviewer said.
     */
    mark?: string;
    why?: string;
  };

import type { Action, Link } from './hateoas.js';
import type { AcceptanceAudit, OverruleAudit } from './observation.js';

/** Server to client. */
export type ServerMessage =
  /**
   * The media credential, sent once at the handshake to a DIRECTOR only.
   *
   * Issue 019: the operator token used to be pasted into the page's own URL, so
   * a director who had already proved themselves at the door was asked to prove
   * it again by hand, and a monitor without it drew a 401 as a black rectangle.
   * Never on the state frame, which every viewer receives.
   */
  | { type: 'operator'; mediaToken: string }
  | {
      type: 'state';
      feeds: FeedView[];
      onAir: OnAir;
      standard: StandardConfig;
      /**
       * Whether the switcher is directing itself.
       *
       * The director's auto-direct checkbox was rendered from local memory:
       * correct on a fresh page, wrong after any reload and wrong for a second
       * director, who would see "off" while the switchboard was directing
       * itself. A control is an advertised action, and one that shows the wrong
       * MODE misdescribes every action on the page around it.
       *
       * The HATEOAS pass found the same defect from the other end, exposed
       * `autoEnabled` so `/api` could report it, and did not wire the frame the
       * director actually reads. Half a fix reads as a whole one once the
       * measurably broken part stops being broken.
       *
       * Required rather than optional so the compiler refuses a state frame that
       * omits it, which is how this stayed missing for a whole wave.
       */
      auto: boolean;
      /**
       * The session's DEFAULT location, or null when none is set.
       *
       * A feed's OWN location, when it has one, rides on the feed. Resolving the
       * two is `effectiveLocation`, done where it is rendered rather than copied
       * onto the feed, so correcting the default mid-event reaches every stream
       * that never overrode it.
       */
      location: Location | null;
      delay: DelayAudit;
      /** Null when no review loop is running. */
      filter: FilterAudit | null;
      /**
       * The last committed BroadcastDecision. Null before the first window
       * closes. Wave 3 renders it; wave 2 only has to produce it.
       */
      decision: BroadcastDecision | null;
      /**
       * Where to go from here, and deliberately NOT what to do.
       *
       * This frame is broadcast to every client twice a second, so the enriched
       * actions belong on `/api/session`, which is fetched when someone wants
       * them. Issue 008 is already about how much work this hot path causes per
       * tick; answering it with five paragraphs of reasons per feed would make a
       * measured problem worse in order to satisfy a principle. The pointer costs
       * nothing and still leaves no dead end: a client holding only this frame
       * can reach everything.
       */
      _links: Record<string, Link>;
      /**
       * The five health signals, kept as five.
       *
       * Proposal open question 13 asks whether they merge into one number. They
       * do not, and this shape is the refusal: one composite is how a red signal
       * gets averaged away by four green ones, and the delay audit is already
       * the signal most likely to be the red one. Scalars only, because this
       * frame goes out twice a second (issue 008).
       */
      health: Health;
    }
  | {
      type: 'ack';
      feedId: string;
      /**
       * The account this feed is attached to, or null for an anonymous
       * publisher. Sent so the page can say which it got: a publisher who
       * signed in and is nonetheless anonymous (expired token, Supabase down)
       * should be able to see that rather than assume.
       */
      handle?: string | null;
      /**
       * Channels this publisher DECLARED that this node keeps no board for, §5.
       *
       * **A registration is a partial success and the protocol had no way to
       * say so.** The feed registered, the stream runs, and one of the two
       * things asked for did not happen: the ack said `feedId` and nothing
       * else, so a publisher offered to `#music` on a node that does not carry
       * it saw "Connected" and appeared on no channel. Silence reads as
       * agreement, and this is the field that stops it.
       *
       * Absent when nothing was refused, so a publisher with no problem carries
       * no field: an empty list beside a populated one is a difference somebody
       * renders and there is nothing to render.
       */
      declined?: readonly { channelId: string; reason: string }[];
    }
  /**
   * A refusal that says why, and what would fix it.
   *
   * Registration used to be refused with `{ type: 'ack', feedId: '' }`: an empty
   * string standing in for an error, carrying no reason and no recovery, in a
   * system whose entire argument is that a refusal must be appealable. The
   * publisher saw nothing at all. Nothing else in the protocol reports a refusal,
   * so this type is the shape for the next one too.
   *
   * `take` joined it on 2026-08-29. A director's refused take was recorded to
   * the session log as `accepted: false` and never sent anywhere: the button
   * did nothing and said nothing. `recovery` may legitimately be empty, because
   * a director cannot fix another operator's gate, and inventing a step they
   * cannot take would be worse than saying there is none.
   */
  /**
   * `what` widened to any client message on 2026-08-29 (wave 5, D9). Privileged
   * messages from an unprivileged socket are refused with the same shape rather
   * than dropped, because silence was already the defect twice: an empty feed id
   * for a refused registration, and a take that recorded a refusal and answered
   * nothing.
   */
  | { type: 'refused'; what: ClientMessage['type']; reason: string; recovery: Action[] }
  /**
   * P5. Pushed to one publisher over the socket it already has, rather than
   * polled: `publish.ts` receives state this way already and a second transport
   * for feedback would be a second thing to keep consistent with what aired.
   *
   * `mutation: 'none'`, so this can be recomputed and re-sent without changing
   * what went to air. Wave 2 produces the payload and stops; wave 3 renders it.
   */
  | { type: 'feedback'; payload: Feedback };

/** Client to server. */
export type ClientMessage =
  | {
      type: 'register';
      path: string;
      /** Which angle this is. See `Feed.angle`; free text, publisher-supplied. */
      angle: string;
      operator: string;
      /**
       * A Supabase access token, when the publisher has signed in. OPTIONAL and
       * permanently so: anyone in the building may offer a feed, and an account
       * buys a handle nobody else can claim and an archive that accumulates,
       * never the right to publish.
       */
      token?: string;
      /**
       * WHICH CHANNELS this stream is offered to, §5 and §7 of the channels plan.
       *
       * **OPTIONAL, and a feed declaring none is on no channel.** That is the
       * plan's own rule, and it is what keeps a private venue working: in EVENT
       * mode nothing reads this and every feed offered to the node is a
       * candidate, which is the case a gym is. In CHANNEL mode it is the only
       * thing that puts a feed in front of a board.
       *
       * Publisher-supplied and unverified, like `angle` and `operator`. It is a
       * CLAIM about where a stream wants to be seen, never gate evidence:
       * `#alaska` is checked against the stream's place id at stage 5, and an
       * unknown id is refused rather than admitted, because a channel that means
       * nothing is worse than one nobody joined.
       */
      channels?: readonly string[];
      /**
       * This stream's OWN location, when it differs from the session's default.
       *
       * OPTIONAL and permanently so. At a gym nobody sends this and every stream
       * inherits one location, which is the case the default exists for. A
       * parade is the case it exists for: those streams are genuinely in
       * different places and the session default is wrong for all but one.
       *
       * Publisher-supplied and unverified, exactly like `label` and `operator`,
       * and for the same reason: this is METADATA, not gate evidence. No gate
       * reads it. The rule that a phone is never asked to supply evidence about
       * itself governs vitals and frames, and applying it to a label was the
       * category error the plan's 2026-08-30 revision corrects.
       *
       * `placeId` is absent here: a publisher types, never picks. Stage 3's
       * picker is the newsroom's surface and the only thing that may write
       * `source: 'picked'`.
       */
      location?: { name: string; locality?: string | null };
    }
  | { type: 'vitals'; vitals: FeedVitals }
  | { type: 'take'; feedId: string }
  | { type: 'slate'; reason: string }
  | { type: 'auto'; enabled: boolean }
  /**
   * Set the session's DEFAULT location. Newsroom only, so it is privileged by
   * `isPrivileged`'s allowlist simply by not being on it.
   *
   * `null` clears it, which is a real operation: a node brought up for the wrong
   * venue should be correctable to "none" rather than to a second wrong guess.
   */
  | { type: 'location'; location: Location | null }
  /**
   * A publisher renaming their own ANGLE, live.
   *
   * The angle is free text so viewers can tell nine streams of one court apart,
   * and a person moves seats. The LOCATION deliberately has no equivalent: it is
   * written into every archive row, so changing it mid-broadcast would make rows
   * before and after the change mean different things about the same stream.
   * Location is fixed at registration and corrected by stopping and starting;
   * the angle is not.
   *
   * Public, and safe by CONSTRUCTION rather than by checking: the server applies
   * it to the socket's own `feedId`, so there is no feed id on the wire to
   * forge. Same shape as `vitals`.
   */
  | { type: 'angle'; angle: string }
  | { type: 'rate'; feedId: string; bad: boolean }
  /**
   * Present a steward's account token, to direct as a named person.
   *
   * PUBLIC in `privilege.ts`, because a connection has to be able to say who it
   * is before it is anybody. The server verifies the token and checks
   * `profiles.is_admin`; presenting one that is not a steward's changes nothing
   * and is answered, not dropped.
   *
   * A message rather than the URL `?key=`, because a JWT in a query string ends
   * up in every access log it passes through, and a steward's token is a
   * credential for their whole account rather than for this page.
   */
  | { type: 'steward'; token: string }
  /**
   * An editor's good/bad call on a feed. Proposal section 11.6.
   *
   * `suggested` is NOT in this message on purpose. The client could send any
   * boolean, and the number it feeds is the one that says whether the evaluation
   * set is still independent of the model. A self-reported independence measure
   * is not a measure, so the server derives it from the verdicts the editor
   * could see.
   */
  | { type: 'label'; feedId: string; good: boolean }
  /**
   * Test seam only. The server ignores this unless
   * SWITCHBOARD_ALLOW_INJECTED_REVIEWS=1, and says so loudly at boot when it
   * does. It exists so the control logic can be exercised without spending on
   * review calls; it must never be enabled anywhere real.
   */
  | { type: 'injectReview'; feedId: string; verdict: Observation };

/**
 * Everything a director needs to see the system's own health, unmerged.
 *
 * `null` on any of these means UNMEASURED, which is not zero. The director's bar
 * once read "LOCAL FILTER UNRELIABLE: missed 350 of 350" on a machine whose
 * judge had never ruled, because an outage was being counted as a finding.
 */
export interface Health {
  /** Gate 1 of 5: does the hold still cover the gates? Judged on the worst, never p95. */
  delay: DelayAudit;
  /** Gate 2 of 5: what the local filter's saving costs. Null when no review loop runs. */
  filter: FilterAudit | null;
  /** Gate 3 of 5: the coach's measured overrule accuracy. Null before any resolve. */
  overrules: OverruleAudit | null;
  /** Gate 4 of 5: did every offered feed get exactly one fate? Null before a window closes. */
  conservation: { offered: number; fates: number; held: boolean } | null;
  /** Gate 5 of 5: how often the editor is agreeing with the machine. */
  acceptance: AcceptanceAudit;
}

/** The written standard, as numbers. Edit this and the broadcast changes. */
export interface StandardConfig {
  minBitrate: number;
  maxPacketLoss: number;
  minFps: number;
  minHeight: number;
  lumaRange: [number, number];
  maxClipping: number;
  maxShake: number;
  audioRmsRange: [number, number];
  maxAudioClipping: number;
  /** Score needed to TAKE a feed that is not currently on air. */
  enterThreshold: number;
  /** Score needed to KEEP a feed that is already on air. Lower, to stop flapping. */
  holdThreshold: number;
  /**
   * How much better a challenger must score before it displaces a healthy
   * on-air feed. `standard-mechanical.md`: clearly better, not luckier.
   */
  switchMargin: number;
  /** Never cut away from a healthy feed sooner than this, in ms. */
  minShotMs: number;
  /** Treat a publisher as gone if no vitals arrive for this long, in ms. */
  staleAfterMs: number;
  /** A verdict older than this no longer authorises anything, in ms. */
  verdictTtlMs: number;
  /** How often to sample frames for visual review, in ms. */
  visualIntervalMs: number;
  /** How often to sample audio for speech review, in ms. */
  speechIntervalMs: number;
  /** Seconds of audio per speech review window. */
  speechWindowSeconds: number;
  /** Number of key frames sent to the visual reviewer per pass. */
  framesPerReview: number;
  /**
   * How much wall clock the picture sample SPANS, in ms. Issue 017.
   *
   * The span is the thing chosen and the frame rate derives from it, which is
   * the inverse of how this worked until 2026-08-31: `framesPerReview` at a
   * fixed `fps=2` produced a span of one second, so the picture gate ruled on
   * one second in twenty and ninety-five percent of what was broadcast was
   * never looked at by gate 1.
   *
   * **The fps constraint was inherited into a place where its reason had gone.**
   * At 1fps the LIVE sampler waits three seconds of wall clock for three frames,
   * and that latency lands on the verdict age. `framesFromSegments` reads
   * recordings already on disk: measured 2026-08-30, 3 frames from 2s of media
   * take 128ms and 3 frames spread over 10s take 180ms. Coverage is nearly free.
   *
   * **What is NOT free is the span**, and that is the whole arithmetic of this
   * number. A verdict carries its sample span, so frames spread over N seconds
   * are N seconds old at their oldest before align, review and select have run:
   * `minVerdictAgeMs` moves one-for-one with this. At the 34s hold it was a
   * one-second-tight trade; issue 016 took the hold to 60s with the floor set
   * by SPEECH at 51s, so 8,000ms is free and lands the picture floor exactly on
   * the speech floor. Both gates then rule on the same eight seconds.
   *
   * The full 20s window is NOT the answer and never was, at any hold this
   * repository has run: a span equal to the window puts the floor above it.
   */
  visualSpanMs: number;
  /**
   * How far behind the live edge the public output is held, in ms. Must exceed
   * observed gate latency or the gates prevent nothing. See shared/delay.ts.
   */
  delayMs: number;
  /**
   * The coordination unit every primitive is defined over. See shared/window.ts.
   *
   * `CALIBRATE`. Must be at least `speechWindowSeconds * 1000` or a window
   * cannot contain a complete speech sample, which would make a per-window
   * speech ruling impossible to produce rather than merely late.
   */
  windowMs: number;
  /**
   * Seconds of audio pulled for the ALIGNMENT fingerprint, which is a different
   * quantity from `speechWindowSeconds` and was measured to be so.
   *
   * A blind correlation sweep honestly reaches only (sample - `minCommonMs`) of
   * offset either side, because the common-audio floor holds per lag rather than
   * on input length. So `maxOffsetMs` of reach needs `maxOffsetMs + minCommonMs`
   * of audio, and an 8s speech window can confirm about 0ms of offset and
   * nothing else. See wave 1 plan section 12.4.
   */
  alignWindowMs: number;

  // --- the four deadlines, one per primitive. All `CALIBRATE`. -------------
  //
  // A barrier that WAITS for a slow feed converts one publisher's problem into
  // everyone's slate, so P2 and P4 have deadlines rather than waits. P1 and P3
  // carry one for the same reason a step later.
  //
  // Read a deadline that turns out to be unmeetable the way proposal section
  // 8.3 asks: the first question is whether the hold is too small, not whether
  // the primitive is too slow. Answered 2026-08-29: the 20s hold sat below the
  // 29s floor these deadlines derive, so no review speed could pass the delay
  // audit, and delayMs moved to 32s inside a band checked at boot
  // (shared/deadlines.ts for the floor, server/media-limits.ts for the
  // ceiling).

  /** P1. Sampling a window per feed: pull, fingerprint, mechanical score. */
  sampleDeadlineMs: number;
  /** P2, the first barrier. Every feed's alignment against the reference. */
  alignDeadlineMs: number;
  /** P3. One feed's two gate reviews, which is where the model calls live. */
  reviewDeadlineMs: number;
  /** P4, the second barrier. Collecting verdicts and deriving the decision. */
  selectDeadlineMs: number;
}

export const DEFAULT_STANDARD: StandardConfig = {
  minBitrate: 600_000,
  maxPacketLoss: 0.05,
  minFps: 15,
  minHeight: 480,
  lumaRange: [0.12, 0.88],
  maxClipping: 0.2,
  maxShake: 0.045,
  // Ranking band, no longer a gate. The floor moved 0.01 -> 0.002 against a
  // measurement rather than a guess: one publisher, one quiet room, 995 samples
  // reading min 0.000145, mean 0.0019, peak 0.0224 with a microphone that was
  // working the whole time. The old floor sat 5x above that mean.
  //
  // This is n=1 on the thing that varies, exactly as `minConfidence` is, and a
  // gym is the case it has NOT been measured against. Re-measure before reading
  // anything into the number; it is now a rank, so being wrong costs position
  // rather than the broadcast.
  audioRmsRange: [0.002, 0.85],
  maxAudioClipping: 0.05,
  enterThreshold: 0.55,
  holdThreshold: 0.4,
  // Was a literal in scoring.ts, then a SECOND literal in derivation.ts, which
  // is exactly the fork this file exists to prevent: standard-mechanical.md names the
  // margin and its header promises every number it names lives here.
  switchMargin: 0.12,
  minShotMs: 6_000,
  staleAfterMs: 6_000,
  // A verdict cannot be younger than the window it was sampled over: capturing
  // 8 seconds of audio takes 8 seconds. So the TTL has to cover
  // window + processing + the gap until the next review, or verdicts expire
  // before anything can refresh them and no feed is ever eligible. Measured
  // that happening: speech verdicts arrived 13s old against a 12s TTL.
  verdictTtlMs: 63_000,
  // Both gate cadences ARE the window, and that is a correctness requirement
  // rather than a tidy-up. The picture gate ran every 8s and the speech gate
  // every 10s, so a per-window `FeedVerdict` would compose a picture ruling and
  // a speech ruling taken from DIFFERENT moments: proposal 2.1's defect one
  // layer down, which A8 exists to prevent. `scripts/units.ts` fails if these
  // drift apart from `windowMs` again.
  visualIntervalMs: 20_000,
  speechIntervalMs: 20_000,
  speechWindowSeconds: 8,
  framesPerReview: 3,
  // Equal to `speechWindowSeconds * 1000` deliberately, so both gates rule on
  // the same span and both floors land on the same number. `scripts/units.ts`
  // holds the floor relationship; this equality is a choice, not a law.
  visualSpanMs: 8_000,
  // Raised from 20s on 2026-08-29, from 32s on 2026-08-30, and from 34s on
  // 2026-08-30. No move was a preference: the hold sits inside a band whose
  // bounds are both DERIVED, and every time a term underneath it moved.
  //
  // The third: `windowMs` and `reviewDeadlineMs` both went to 20s to close
  // issue 016, and the floor carries the review deadline AND the refresh
  // interval, so the speech floor went 33s -> 51s. This is the first move that
  // needed the CEILING raised too: `hlsSegmentCount` went 50 -> 70 in
  // `mediamtx.yml`, making the usable ceiling 65s (`holdFits` takes a 5s
  // margin off the window). **A hold larger than the HLS
  // window silently becomes a SMALLER hold**, because the viewer's seek clamps
  // to the start of the seekable range, so this is the one bound that fails
  // quietly rather than loudly. Both are checked at boot.
  //
  // 51 seconds is the derived FLOOR, not the hold; the hold is the 60 below and
  // that is the product decision. The floor buys a review deadline that
  // covers the worst escalated review observed (18.9s local, 15.5s on the
  // hosted node) rather than its median, which is the same standard this
  // repository already applies to delay sufficiency.
  //
  // The first: 20s was sized from one picture review at p95 and sat below the
  // 29s speech floor, so the delay audit could not pass at any review speed.
  //
  // The second: `reviewDeadlineMs` went to 12s for the router (see below), and
  // the floor carries the review deadline, so it rose from 29s to 33s. 34s is
  // one second over that floor and eleven under the usable ceiling, which
  // `server/media-limits.ts` reads from the media plane's own config rather
  // than from a second copy of the number here. Both bounds checked at boot.
  //
  // **60 and not 52, and this one IS a choice.** 52 was "one second over the
  // floor", which is a defensive number rather than a designed one: it leaves
  // the hold one second from having to move again the next time anything
  // underneath it does. 60 sits 9 seconds over the floor and 5 under the usable
  // ceiling, so issue 017's wider picture span and a future deadline bump both
  // fit without touching it.
  //
  // The band is checked with STRICT inequalities either way, because a hold
  // equal to the floor expires a verdict in the same instant the material it
  // ruled on is displayed.
  //
  // And it is a sentence a venue can hold: "a minute behind live". This number
  // is published on the standard page, so how it reads is part of what it is.
  // **30 seconds was asked for and is impossible**: it is 21 seconds under the
  // derived floor, which would make gates 1 and 2 commentary on material the
  // audience has already seen.
  delayMs: 60_000,
  // Matched to speechIntervalMs so one review cadence falls per window, and
  // above speechWindowSeconds (8s) because a window shorter than the sample it
  // has to contain cannot hold a complete speech ruling. Both are the floor,
  // not a measurement: CALIBRATE.
  //
  // 10s -> 20s on 2026-08-30, for issue 016. **A longer window is not more time
  // for free**: `refreshIntervalMs` IS the window and it is a term in
  // `minVerdictAgeMs`, so every second here is also a second on the floor and
  // therefore on the hold. What it buys is that a review may take as long as
  // the interval between reviews of the same feed, which is what caps
  // `reviewDeadlineMs`.
  //
  // **It halves the picture gate's coverage and that is issue 017 getting
  // worse.** The span stays 1s (`framesPerReview` 3 at fps=2) while the window
  // doubles, so gate 1 now rules on 1 second in 20 rather than 1 in 10. The
  // fix is a spread rather than more frames, and it is deliberately not made
  // here: this change is a deadline, not a change to what the gate looks at.
  windowMs: 20_000,
  // maxOffsetMs (4s) + minCommonMs (8s). NOT a chosen number: it is the shortest
  // sample from which a blind sweep can honestly reach the full plausible
  // transport skew, and 8s of audio reaches about 0ms. Affordable only because
  // sampling reads recorded segments: 12s of audio decodes in 0.079s against
  // 8.14s to wait for 8s live. Measured 2026-08-29.
  alignWindowMs: 12_000,
  // Sized for the segment path, measured at 0.070 to 0.079s for both the audio
  // and the frames, so this is roughly 40x headroom. The live RTSP fallback
  // does NOT fit it, which is deliberate and loud: it costs 8.14s for the same
  // audio, and a deadline generous enough to hide that would hide the finding.
  sampleDeadlineMs: 3_000,
  // Pure CPU over fingerprints already in memory. CALIBRATE, and measured at
  // runtime by the pipeline rather than assumed.
  alignDeadlineMs: 1_500,
  // The only stage with a model in it. Two gates in parallel, and the picture
  // gate is itself two calls at 5.6s median (describe then judge). CALIBRATE.
  //
  // 8s rather than the 12s a first pass assumed, because SAMPLING LEFT THIS
  // STAGE: the measured 8.3s median local speech latency was almost entirely the
  // 8s window wait, which P1 now absorbs by reading segments. What remains here
  // is model time, and the cascade skips the judge on 79-87% of windows.
  //
  // **Back to 12s on 2026-08-30, and the reason is a different model rather than
  // a slower one.** 8s was right for the local pair, whose picture review runs a
  // median around 4.7s. The hosted node runs the same weights' nearest
  // available relatives through a router at a median of 8.2s, which misses an
  // 8s deadline with ONE publisher and no contention at all. Observed on the
  // deployed node: `missed the 8000ms review deadline`.
  //
  // Concurrency does not touch this. A single call that takes 8.2s misses an 8s
  // deadline whether it is alone or one of eight, which is the distinction the
  // capacity model does not make and issue 004 now does.
  //
  // 12s -> 20s on 2026-08-30, closing issue 016 against the TAIL rather than
  // the median. The sentence above it stood for exactly one day: 12s "covers
  // the router's median with room and does NOT cover the 15.1s worst of five
  // samples, which is the number to re-measure over a real run". It was
  // re-measured over a real run, by an operator streaming into the hosted node:
  // worst gate 15.5s over n=20, and an eligible feed with zero blockers sat on
  // the slate reading `missed the 12000ms review deadline`.
  //
  // A review may not outlast the interval between reviews of the same feed, so
  // this is capped by `windowMs` and moves with it. Both went to 20s together,
  // which is why the floor rose 18s rather than 8s: the deadline counts once
  // and the refresh interval counts again.
  reviewDeadlineMs: 20_000,
  // A derivation over verdicts already collected. Same shape as align.
  selectDeadlineMs: 1_500,
};
